/**
 * SignalLedger — persistent 340px right column of raw-signal arrivals.
 *
 * Sprint-2 Task 2.5 (sprint.md:235). Header bucket pills (CRIT/HIGH/MOD)
 * + age timer. Per-row: SIG-NNN + timestamp · domain pill + headline +
 * severity-hue left border + optional modifier chip. Footer: "N of M
 * require attention".
 *
 * A11y per sprint.md AC-8:
 *   * aria-live="polite" announces NEW signal arrivals only (not age
 *     ticks). Implemented via a separate live-region that re-renders
 *     when the arrival-digest key changes.
 *   * Header has 30s summary line for screen readers — read via a
 *     `role=status` element that surfaces the bucket counts in prose.
 */

import { useEffect, useRef, useState } from 'react';

export type SignalSeverity = 'critical' | 'high' | 'moderate' | 'low';

/** Fidelity tier of the underlying OsintSignal.
 *
 * `'raw'` — direct upstream feed (e.g. WorldMonitor live emit, exchange
 *   tick). PRD §Success-criteria #5 Bullet 1 requires the Ledger to mark
 *   raw arrivals visibly so the operator can verify the flywheel.
 * `'composite'` — derived/scored/aggregated by an Echelon collector.
 *
 * Sprint-3 surface for cycle-101.1 (beads `171x`); sprint-2 + earlier
 * left this field absent. Callers MAY omit it — the renderer treats
 * `undefined` the same as `'composite'` (no marker).
 */
export type SignalFidelity = 'raw' | 'composite';

export interface SignalRow {
  readonly id: string;
  /** ISO8601 timestamp of arrival. */
  readonly receivedAt: string;
  readonly domain: string;
  readonly headline: string;
  readonly severity: SignalSeverity;
  /** Optional inline chip label (e.g. +3 cluster, +OSINT). */
  readonly modifier?: string;
  /** Whether this signal requires operator attention (for footer count). */
  readonly requiresAttention?: boolean;
  /** Fidelity tier — drives the "RAW" kicker chip in the row. */
  readonly fidelity?: SignalFidelity;
}

export interface SignalLedgerProps {
  signals: readonly SignalRow[];
  /** Called when a signal row is clicked — session-2 shell opens SignalDrawer. */
  onSelectSignal?: (id: string) => void;
  /** Currently selected signal id (drives active highlight). */
  selectedId?: string;
  className?: string;
}

const SEVERITY_HUES: Record<SignalSeverity, string> = {
  critical: '--cd-sig-rose',
  high: '--cd-sig-ember',
  moderate: '--cd-sig-amber',
  low: '--cd-sig-mint',
};

const SEVERITY_LABELS: Record<SignalSeverity, string> = {
  critical: 'CRIT',
  high: 'HIGH',
  moderate: 'MOD',
  low: 'LOW',
};

export function SignalLedger({ signals, onSelectSignal, selectedId, className }: SignalLedgerProps) {
  const critCount = signals.filter((s) => s.severity === 'critical').length;
  const highCount = signals.filter((s) => s.severity === 'high').length;
  const modCount = signals.filter((s) => s.severity === 'moderate').length;
  const attentionCount = signals.filter((s) => s.requiresAttention).length;

  // Track newest arrival for aria-live announcement. Recompute only on
  // the topmost id changing (new arrivals push to top).
  const topId = signals[0]?.id;
  const prevTopId = useRef<string | undefined>(undefined);
  const [liveMessage, setLiveMessage] = useState('');
  useEffect(() => {
    if (topId && topId !== prevTopId.current && prevTopId.current !== undefined) {
      const top = signals[0];
      setLiveMessage(`New signal ${top.id}: ${top.headline}`);
    }
    prevTopId.current = topId;
  }, [topId, signals]);

  return (
    <aside
      role="complementary"
      aria-label="Signal ledger"
      className={className}
      style={{
        width: 340,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--cd-bg-ledger)',
        borderLeft: '1px solid var(--cd-seam)',
        fontFamily: 'var(--cd-font-sans)',
        position: 'relative',
        boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.03)',
      }}
    >
      {/* HUD corner ticks */}
      <CornerTick position="tl" />
      <CornerTick position="bl" />

      {/* Sticky header — bucket pills */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          background: 'var(--cd-bg-card-header)',
          borderBottom: '1px solid var(--cd-border-strong)',
          padding: '10px 14px 9px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          boxShadow: '0 1px 0 rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span
            style={{
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 10,
              color: 'var(--cd-fg)',
              letterSpacing: '0.16em',
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            Signal Ledger
          </span>
          <span
            style={{
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 10,
              color: 'var(--cd-fg-muted)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {signals.length}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <BucketPill count={critCount} label="CRIT" hueVar="--cd-sig-rose" />
          <BucketPill count={highCount} label="HIGH" hueVar="--cd-sig-ember" />
          <BucketPill count={modCount} label="MOD" hueVar="--cd-sig-amber" />
        </div>
        {/* 30s summary line for screen readers (AC-8) */}
        <div
          role="status"
          aria-live="off"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {`Ledger summary: ${critCount} critical, ${highCount} high, ${modCount} moderate, ${signals.length} total.`}
        </div>
      </div>

      {/* Arrival announcer — aria-live="polite" on the TOPMOST change only.
          Age ticks do NOT re-render this node. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {liveMessage}
      </div>

      {/* Scrollable body */}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {signals.map((sig) => (
          <SignalRowView
            key={sig.id}
            signal={sig}
            selected={sig.id === selectedId}
            onSelect={onSelectSignal}
          />
        ))}
      </ul>

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid var(--cd-border)',
          padding: '8px 14px',
          background: 'var(--cd-bg-card-header)',
          fontFamily: 'var(--cd-font-mono)',
          fontSize: 10,
          color: 'var(--cd-fg-secondary)',
          letterSpacing: '0.08em',
        }}
      >
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{attentionCount}</span>
        <span> of </span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{signals.length}</span>
        <span> require attention</span>
      </div>
    </aside>
  );
}

// ── Bucket pill ─────────────────────────────────────────────────────────

interface BucketPillProps {
  count: number;
  label: string;
  hueVar: string;
}

function BucketPill({ count, label, hueVar }: BucketPillProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 9.5,
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
        fontWeight: 500,
        color: count > 0 ? `var(${hueVar})` : 'var(--cd-fg-muted)',
        border: `1px solid ${count > 0 ? `var(${hueVar})` : 'var(--cd-border)'}`,
        borderRadius: 2,
        padding: '1px 6px',
      }}
    >
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      <span>{label}</span>
    </span>
  );
}

// ── Signal row ──────────────────────────────────────────────────────────

interface SignalRowViewProps {
  signal: SignalRow;
  selected: boolean;
  onSelect?: (id: string) => void;
}

function SignalRowView({ signal: s, selected, onSelect }: SignalRowViewProps) {
  const hue = SEVERITY_HUES[s.severity];
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect?.(s.id)}
        aria-label={`${s.id} ${SEVERITY_LABELS[s.severity]} ${s.domain}${s.fidelity === 'raw' ? ' RAW' : ''}: ${s.headline}`}
        aria-current={selected ? 'true' : undefined}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: '10px 12px 10px 14px',
          background: selected ? 'var(--cd-bg-hover)' : 'transparent',
          border: 'none',
          borderLeft: `3px solid var(${hue})`,
          borderBottom: '1px solid var(--cd-seam)',
          textAlign: 'left',
          cursor: 'pointer',
          color: 'var(--cd-fg)',
          transition: 'background .12s',
        }}
      >
        {/* Row 1: id + timestamp + domain pill + optional RAW kicker */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'var(--cd-font-mono)',
            fontSize: 9.5,
            color: 'var(--cd-fg-muted)',
            letterSpacing: '0.06em',
          }}
        >
          <span style={{ color: 'var(--cd-fg-secondary)', fontVariantNumeric: 'tabular-nums' }}>
            {s.id}
          </span>
          <span aria-hidden="true">·</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(s.receivedAt)}</span>
          <span aria-hidden="true">·</span>
          <DomainPill domain={s.domain} />
          {/* Fidelity marker — only renders when explicitly raw, per
              PRD §Success-criteria #5 Bullet 1 (cycle-101.1 sprint-3
              beads `171x`). Iris accent matches the FORGE lane source-
              group accent — a stable visual hook for the "raw → composite"
              pipeline. */}
          {s.fidelity === 'raw' && (
            <span
              data-testid="signal-row-fidelity"
              data-fidelity="raw"
              style={{
                fontFamily: 'var(--cd-font-mono)',
                fontSize: 8.5,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                color: 'var(--cd-sig-iris)',
                border: '1px solid var(--cd-sig-iris)',
                borderRadius: 2,
                padding: '0 4px',
              }}
            >
              RAW
            </span>
          )}
        </div>
        {/* Row 2: headline + modifier chip */}
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.35,
            color: 'var(--cd-fg)',
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {s.headline}
          </span>
          {s.modifier && (
            <span
              style={{
                fontFamily: 'var(--cd-font-mono)',
                fontSize: 9,
                letterSpacing: '0.06em',
                color: 'var(--cd-fg-secondary)',
                border: '1px solid var(--cd-border)',
                borderRadius: 2,
                padding: '1px 4px',
                flexShrink: 0,
              }}
            >
              {s.modifier}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

// ── Domain pill ─────────────────────────────────────────────────────────

function DomainPill({ domain }: { domain: string }) {
  return (
    <span
      style={{
        fontFamily: 'var(--cd-font-mono)',
        fontSize: 8.5,
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
        color: 'var(--cd-fg-muted)',
        border: '1px solid var(--cd-border)',
        borderRadius: 2,
        padding: '0 4px',
      }}
    >
      {domain}
    </span>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  const date = new Date(iso);
  const hh = date.getHours().toString().padStart(2, '0');
  const mm = date.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function CornerTick({ position }: { position: 'tl' | 'bl' }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: 0,
        [position === 'tl' ? 'top' : 'bottom']: 0,
        width: 10,
        height: 10,
        borderLeft: '1px solid var(--cd-border-strong)',
        [position === 'tl' ? 'borderTop' : 'borderBottom']: '1px solid var(--cd-border-strong)',
      }}
    />
  );
}
